// ===================================
// KAIXO - Vista inicial: Contactos del teléfono
// Lista los contactos del celular del usuario:
//   - arriba: los que tienen KAIXO instalado (chat directo al pulsar)
//   - abajo: los que no, con botón de invitar
// En web (sin Capacitor) muestra fallback amable.
// ===================================

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Icons } from '../Icons/Icons';
import { syncContacts as apiSyncContacts, getContacts as apiGetContacts } from '../../services/api';
import t from '../../i18n/useTranslation';
import { formatRelativeDate } from '../../utils/formatDate';
import './PhoneContactsView.css';

let ContactsPlugin = null;
try {
    // eslint-disable-next-line global-require, import/no-unresolved
    ContactsPlugin = require('@capacitor-community/contacts').Contacts;
} catch {}

let Capacitor = null;
try {
    // eslint-disable-next-line global-require
    Capacitor = require('@capacitor/core').Capacitor;
} catch {}

let SharePlugin = null;
try {
    // eslint-disable-next-line global-require, import/no-unresolved
    SharePlugin = require('@capacitor/share').Share;
} catch {}

function checkIsNative() {
    try {
        return !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform());
    } catch { return false; }
}

// Caché local de la sección "En KAIXO" + Support para que la siguiente
// apertura pinte instantáneo, sin esperar a que el backend resincronice
// toda la agenda. Se sobreescribe en cada getContacts exitoso, así que
// nunca queda obsoleto más allá del primer ~segundo de la apertura.
const CACHE_KEY = (uid) => `kaixo_phone_contacts_v1:${uid}`;
const AGENDA_HASH_KEY = (uid) => `kaixo_phone_agenda_hash_v1:${uid}`;

// Hash compacto y rápido de la agenda (djb2 sobre teléfonos+nombres).
// Si el hash coincide con el último sync, podemos SALTAR apiSyncContacts
// — la agenda no cambió, en backend ya está la misma información.
function quickAgendaHash(devContacts) {
    let h = 5381;
    for (const c of devContacts) {
        const s = (c.phone || '') + ' ' + (c.name || '');
        for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) | 0;
    }
    return String(h);
}

function PhoneContactsView({ currentUser, onSelectConversation, onlineUsers, conversations = [], groups = [], onAddDeviceContact }) {
    const [withKaixo, setWithKaixo] = useState([]);
    const [withoutKaixo, setWithoutKaixo] = useState([]);
    const [supportContact, setSupportContact] = useState(null);

    // Modo "invitación múltiple" para la sección "Otros contactos del teléfono".
    // Cuando está OFF, la sección es solo informativa (sin botones azules).
    // Cuando está ON, cada fila muestra un checkbox y arriba aparece la
    // acción "Invitar a N" que abre SMS con todos los seleccionados.
    const [bulkInviteMode, setBulkInviteMode] = useState(false);
    const [selectedInvites, setSelectedInvites] = useState(new Set());

    const toggleInviteSelected = (id) => {
        setSelectedInvites(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const exitBulkInvite = () => { setBulkInviteMode(false); setSelectedInvites(new Set()); };
    const [loading, setLoading] = useState(true);
    const [permissionState, setPermissionState] = useState('unknown'); // unknown | granted | denied | unsupported | empty
    const [errorMsg, setErrorMsg] = useState('');
    const [search, setSearch] = useState('');
    const [refreshTick, setRefreshTick] = useState(0);
    // Diagnóstico visible al usuario para depurar bugs ("solo veo Support")
    const [diag, setDiag] = useState({ devCount: 0, syncOk: null, getOk: null });
    const syncedOnceRef = useRef(false);
    // Lock para evitar que loadAndSync corra concurrente cuando appStateChange
    // o kaixo_contacts_changed disparan setRefreshTick mientras una carga aún
    // está en curso. Sin esto, una carga interrumpe a otra y el render parpadea.
    const loadingInProgressRef = useRef(false);
    // Timestamp del último refresh consumido. Debounce contra appStateChange,
    // que en algunos Android se dispara al abrir/cerrar el diálogo de permisos
    // del sistema y crearía un bucle de re-cargas.
    const lastRefreshAtRef = useRef(0);

    const labels = useMemo(() => ({
        title: t.contacts?.title || 'Contactos',
        searchPlaceholder: t.contacts?.search || 'Buscar...',
        withKaixo: t.contacts?.withKaixo || 'En KAIXO',
        invite: t.contacts?.invite || 'Invitar a KAIXO',
        inviteAll: t.contacts?.inviteAll || 'Invitar a todos',
        emptyTitle: t.contacts?.emptyTitle || 'Aún no se ven contactos',
        emptyDesc: t.contacts?.emptyDesc || 'Permite a KAIXO leer tu agenda para encontrar a quién ya está dentro.',
        newBadge: t.contacts?.newBadge || 'NUEVO',
        supportSection: t.contacts?.supportSection || 'Asistencia',
        supportSub: t.contacts?.supportSub || 'Cuenta oficial — abre un chat para escribirnos',
        permissionBtn: t.contacts?.permissionBtn || 'Permitir acceso a contactos',
        permissionDeniedTitle: t.contacts?.permissionDeniedTitle || 'Permiso denegado',
        permissionDeniedDesc: t.contacts?.permissionDeniedDesc || 'Abre Ajustes → KAIXO → Permisos → Contactos para activarlo.',
        webOnlyTitle: t.contacts?.webOnlyTitle || 'Disponible en la app móvil',
        webOnlyDesc: t.contacts?.webOnlyDesc || 'Para ver tus contactos del teléfono instala KAIXO en Android o iOS.',
        retry: t.contacts?.retry || 'Reintentar',
        loading: t.contacts?.loading || 'Cargando contactos...',
        syncing: t.contacts?.syncing || 'Sincronizando...',
        noResults: t.contacts?.noResults || 'Sin resultados',
        noKaixoYet: t.contacts?.noKaixoYet || 'Ninguno de tus contactos tiene KAIXO todavía. Invítales abajo.',
        kaixoBadge: t.contacts?.kaixoBadge || 'KAIXO',
        otherContacts: t.contacts?.otherContacts || 'Otros contactos del teléfono'
    }), []);

    const inviteUrl = useMemo(() => {
        const base = (process.env.REACT_APP_PUBLIC_URL || 'https://kaixo.ai').replace(/\/+$/, '');
        const handle = currentUser?.handle;
        return handle ? `${base}/c/@${handle}` : base;
    }, [currentUser]);

    const inviteText = useMemo(() => (
        t.contacts?.inviteText
            || `¡Te invito a KAIXO! Habla con cualquier persona del mundo y cada uno lee en su idioma. Descárgala: ${inviteUrl}`
    ), [inviteUrl]);

    const loadAndSync = async () => {
        if (!currentUser?._id) {
            setLoading(false);
            return;
        }
        // Evita ejecuciones concurrentes que provocan parpadeo de la
        // sección "En KAIXO" (carga A pinta cache → carga B borra cache
        // → carga A backend → carga B backend...). Con el lock, las
        // refrescos extra durante una carga en curso se descartan.
        if (loadingInProgressRef.current) return;
        loadingInProgressRef.current = true;
        lastRefreshAtRef.current = Date.now();
        setErrorMsg('');

        try { // wrapper para liberar lock pase lo que pase

        // FASE 0 — pintar caché local INSTANTÁNEO si existe. La sección
        // "En KAIXO" aparece sin esperar a la red. El refresh real va en
        // paralelo y sobreescribe cuando llegue.
        let hadCache = false;
        try {
            const raw = localStorage.getItem(CACHE_KEY(currentUser._id));
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data?.withKaixo)) {
                    setWithKaixo(data.withKaixo);
                    hadCache = true;
                }
                if (data?.support) setSupportContact(data.support);
                if (Array.isArray(data?.withoutKaixo)) setWithoutKaixo(data.withoutKaixo);
            }
        } catch {}
        if (hadCache) {
            setLoading(false);
            setPermissionState('granted'); // si había caché, el último intento concedió permiso
        } else {
            setLoading(true);
        }

        // Evaluar Capacitor en cada carga (no en module load) para evitar
        // estados estancados si el plugin se inicializó después del montaje.
        const isNative = checkIsNative();
        if (!isNative || !ContactsPlugin) {
            // En web no tenemos acceso a la agenda del móvil, pero todavía
            // podemos pedir al backend la lista de contactos sincronizados
            // previamente (por si el usuario ya usó la app móvil antes) y
            // mostrarlos. Lo mejor disponible.
            setPermissionState('unsupported');
            try {
                const res = await apiGetContacts(currentUser._id);
                const data = res?.data?.contacts || { withKaixo: [], withoutKaixo: [], support: null };
                const wk = Array.isArray(data.withKaixo) ? data.withKaixo : [];
                const wok = Array.isArray(data.withoutKaixo) ? data.withoutKaixo : [];
                setWithKaixo(wk);
                setWithoutKaixo(wok);
                setSupportContact(data.support || null);
                try {
                    localStorage.setItem(CACHE_KEY(currentUser._id), JSON.stringify({
                        withKaixo: wk, withoutKaixo: wok, support: data.support || null, savedAt: Date.now()
                    }));
                } catch {}
            } catch (e) {
                console.warn('getContacts (web fallback):', e?.message);
            }
            setLoading(false);
            return;
        }

        try {
            let status = null;
            try {
                if (ContactsPlugin.checkPermissions) {
                    status = await ContactsPlugin.checkPermissions();
                }
            } catch {}

            if (!status || status.contacts !== 'granted') {
                const req = await ContactsPlugin.requestPermissions();
                if (req?.contacts !== 'granted') {
                    setPermissionState(req?.contacts === 'denied' ? 'denied' : 'unknown');
                    setLoading(false);
                    return;
                }
            }
            setPermissionState('granted');

            // PASO 1 — leer contactos del dispositivo (igual que InviteFriendsModal)
            const result = await ContactsPlugin.getContacts({
                projection: { name: true, phones: true }
            });
            const raw = (result && result.contacts) || [];

            const devContacts = raw
                .map((c, idx) => {
                    const allPhones = Array.isArray(c.phones) ? c.phones : [];
                    const primaryPhone = allPhones.find(p => p?.isPrimary) || allPhones[0];
                    const number = primaryPhone?.number || '';
                    const display = c.name?.display || c.name?.given || c.name?.family || '(sin nombre)';
                    return {
                        id: c.contactId || `dev-${idx}-${number}`,
                        name: display,
                        phone: number
                    };
                })
                .filter(c => c.phone && c.phone.trim())
                .sort((a, b) => a.name.localeCompare(b.name));

            setDiag(d => ({ ...d, devCount: devContacts.length }));

            if (devContacts.length === 0) {
                setPermissionState('empty');
                setLoading(false);
                return;
            }

            // PASO 2 — MOSTRAR YA los contactos del dispositivo aunque el backend
            // tarde o falle. La lista nunca queda vacía si tienes agenda.
            //
            // IMPORTANTE: si en FASE 0 pintamos caché, NO reseteamos `withKaixo`
            // ni `withoutKaixo`. Si lo hiciéramos, el usuario vería los contactos
            // KAIXO desaparecer mientras la lista del dispositivo se llena de
            // contactos sin badge — un parpadeo visible. Conservamos lo de la
            // caché hasta que PASO 3 traiga datos frescos del backend.
            if (!hadCache) {
                setWithKaixo([]);
                setWithoutKaixo(devContacts.map(c => ({
                    id: c.id,
                    name: c.name,
                    phone: c.phone,
                    hasKaixo: false
                })));
            }
            setLoading(false);
            syncedOnceRef.current = true;

            // PASO 3 — sincronizar con backend para enriquecer con badge KAIXO.
            // Si esto falla, el usuario sigue viendo su agenda completa abajo.
            //
            // Optimización: la agenda no cambia entre aperturas en el 99% de
            // los casos. Hasheamos los contactos del dispositivo y comparamos
            // con el último sync. Si coincide, SALTAMOS apiSyncContacts (que
            // hace bulkWrite + deleteMany — caro) y vamos directo a
            // apiGetContacts. Esto reduce de 2 RTT a 1 RTT la carga típica.
            await (async () => {
                const agendaHash = quickAgendaHash(devContacts);
                const lastHash = localStorage.getItem(AGENDA_HASH_KEY(currentUser._id));
                const agendaChanged = lastHash !== agendaHash;

                if (agendaChanged) {
                    try {
                        await apiSyncContacts({
                            userId: currentUser._id,
                            contacts: devContacts.map(c => ({ name: c.name, phone: c.phone }))
                        });
                        try { localStorage.setItem(AGENDA_HASH_KEY(currentUser._id), agendaHash); } catch {}
                        setDiag(d => ({ ...d, syncOk: true }));
                    } catch (e) {
                        console.warn('syncContacts (background):', e?.message);
                        setDiag(d => ({ ...d, syncOk: false }));
                    }
                } else {
                    // Marcamos diag como ok aunque saltamos: el sync previo
                    // sigue siendo válido porque la agenda es idéntica.
                    setDiag(d => ({ ...d, syncOk: true }));
                }

                try {
                    const res = await apiGetContacts(currentUser._id);
                    const data = res?.data?.contacts || { withKaixo: [], withoutKaixo: [], support: null };
                    setSupportContact(data.support || null);
                    setDiag(d => ({ ...d, getOk: true }));

                    // Mapear contactos del backend por hash/teléfono normalizado
                    const cleanPhone = p => String(p || '').replace(/\D/g, '').replace(/^00/, '').replace(/^\+/, '');
                    const kaixoByCleaned = new Map();
                    for (const k of (data.withKaixo || [])) {
                        const c = cleanPhone(k.phone);
                        if (c) kaixoByCleaned.set(c, k);
                    }

                    // Reclasificar la lista del dispositivo:
                    // - si el teléfono limpio matchea con un withKaixo → arriba
                    // - si no → otros contactos
                    //
                    // Dedup: si la agenda del móvil tiene el mismo número guardado
                    // en varios formatos ("+34 653 53 23 47", "0034653532347",
                    // "653 53 23 47"), todas matchean el MISMO usuario Kaixo.
                    // Sin esta dedup, el contacto aparecía repetido tantas veces
                    // como entradas hubiera en la agenda (mismo Tomy x N).
                    const newWithKaixo = [];
                    const newWithoutKaixo = [];
                    const seenKaixoUserIds = new Set();
                    const seenWithoutKaixoSuffix = new Set();
                    for (const dev of devContacts) {
                        const c = cleanPhone(dev.phone);
                        let match = kaixoByCleaned.get(c);
                        if (!match && c.length >= 9) {
                            // Match estricto por SUFIJO NACIONAL: últimos 9 dígitos
                                // exactos. Antes aceptábamos `last9.endsWith(key)` lo que
                            // permitía que un Kaixo con phone corto matcheara con
                            // cualquier número largo terminado en esos dígitos
                            // (falso positivo). Ahora exigimos que ambos tengan
                            // ≥9 dígitos y que sus últimos 9 dígitos coincidan.
                            const last9 = c.slice(-9);
                            for (const [key, val] of kaixoByCleaned) {
                                if (key.length >= 9 && key.slice(-9) === last9) { match = val; break; }
                            }
                        }
                        if (match) {
                            const kaixoUserId = (match.kaixoUser?.id || match.kaixoUser?._id || match._id || match.id || '').toString();
                            // Si ya emitimos a este usuario Kaixo, saltar (otra entrada
                            // de la agenda apuntaba al mismo usuario por otro formato).
                            if (kaixoUserId && seenKaixoUserIds.has(kaixoUserId)) continue;
                            if (kaixoUserId) seenKaixoUserIds.add(kaixoUserId);
                            newWithKaixo.push({
                                ...match,
                                id: dev.id,
                                name: match.kaixoUser?.name || dev.name,
                                phone: dev.phone,
                                hasKaixo: true
                            });
                        } else {
                            // Dedup también para "sin Kaixo": mismo número en varios
                            // formatos no debería aparecer N veces. Clave: sufijo nacional.
                            const last9 = c.slice(-9);
                            const key = last9 || c || dev.phone;
                            if (seenWithoutKaixoSuffix.has(key)) continue;
                            seenWithoutKaixoSuffix.add(key);
                            newWithoutKaixo.push({ id: dev.id, name: dev.name, phone: dev.phone, hasKaixo: false });
                        }
                    }
                    // Recientes primero
                    newWithKaixo.sort((a, b) => {
                        if (a.recentlyJoined && !b.recentlyJoined) return -1;
                        if (!a.recentlyJoined && b.recentlyJoined) return 1;
                        return (a.name || '').localeCompare(b.name || '');
                    });
                    setWithKaixo(newWithKaixo);
                    setWithoutKaixo(newWithoutKaixo);

                    // Guardar caché para próxima apertura instantánea.
                    // Solo lo crítico para pintar arriba (En KAIXO + Support).
                    // withoutKaixo no lo guardamos: lo regenera la lectura
                    // nativa de la agenda en cada apertura.
                    try {
                        localStorage.setItem(CACHE_KEY(currentUser._id), JSON.stringify({
                            withKaixo: newWithKaixo,
                            support: data.support || null,
                            savedAt: Date.now()
                        }));
                    } catch {}
                } catch (e) {
                    console.warn('getContacts (background):', e?.message);
                    setDiag(d => ({ ...d, getOk: false }));
                }
            })();
        } catch (err) {
            console.error('PhoneContactsView load error:', err);
            setErrorMsg(err?.message || 'Error desconocido');
            setLoading(false);
        }

        } finally {
            // Liberar el lock pase lo que pase. Hasta aquí PASO 3 ya completó
            // (lo awaiteamos arriba), así que el siguiente refreshTick no
            // pisará una carga viva.
            loadingInProgressRef.current = false;
        }
    };

    useEffect(() => {
        loadAndSync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTick, currentUser?._id]);

    // Re-sync cuando se añade un contacto al móvil desde otro componente.
    useEffect(() => {
        const handler = () => setRefreshTick(t => t + 1);
        window.addEventListener('kaixo_contacts_changed', handler);
        return () => window.removeEventListener('kaixo_contacts_changed', handler);
    }, []);

    // Re-sync cuando la app vuelve a foreground. Caso típico: el usuario
    // sale a la agenda del móvil, borra/edita contactos y vuelve a KAIXO.
    // Sin esto la lista quedaría desactualizada hasta el siguiente reload.
    //
    // Debounce: en algunos Android (Samsung One UI, MIUI) el diálogo de
    // permisos del sistema dispara appStateChange isActive=false→true en
    // cuestión de ms, y sin debounce eso provocaba un bucle de re-cargas
    // que veía el usuario como un parpadeo continuo de la sección "En KAIXO".
    useEffect(() => {
        let sub;
        (async () => {
            try {
                const { App } = await import('@capacitor/app');
                sub = await App.addListener('appStateChange', (state) => {
                    if (!state?.isActive) return;
                    const now = Date.now();
                    if (now - lastRefreshAtRef.current < 8000) return;
                    setRefreshTick(t => t + 1);
                });
            } catch {} // entornos no-Capacitor (web): sin foreground hook
        })();
        return () => { try { sub?.remove?.(); } catch {} };
    }, []);

    // Mapa otherUser.id → conversación existente. Si el usuario tiene un chat
    // abierto con un contacto, queremos reutilizarlo (no crear uno nuevo).
    const conversationByUserId = useMemo(() => {
        const map = new Map();
        for (const c of (conversations || [])) {
            const oid = c?.otherUser?.id;
            if (oid) map.set(String(oid), c);
        }
        return map;
    }, [conversations]);

    // Conversaciones cuyo emisor NO está en la agenda del usuario (mensajes
    // de gente desconocida). Las mostramos en su propia sección arriba con
    // pill de aviso + botón para añadir a contactos del móvil.
    const conversationsNotInContacts = useMemo(
        () => (conversations || []).filter(c => c.senderNotInContacts && !c?.otherUser?.isOfficial),
        [conversations]
    );

    const handleOpenChat = (kaixoUser) => {
        if (!kaixoUser?.id || !onSelectConversation) return;
        const existing = conversationByUserId.get(String(kaixoUser.id));
        if (existing) {
            // Reusar conversación existente: pasar el ID real
            onSelectConversation(existing);
            return;
        }
        onSelectConversation({
            id: null,
            type: 'direct',
            otherUser: {
                id: kaixoUser.id,
                name: kaixoUser.name,
                avatar: kaixoUser.avatar,
                language: kaixoUser.language,
                region: kaixoUser.region
            },
            lastMessage: '',
            unreadCount: 0,
            isNew: true
        });
    };

    const handleInvite = async (contact) => {
        const text = inviteText;
        if (SharePlugin && checkIsNative()) {
            try {
                await SharePlugin.share({ title: 'KAIXO', text, dialogTitle: 'Compartir invitación' });
                return;
            } catch {}
        }
        // Fallback: abrir SMS con número prefilled (Android intent funciona en webview)
        if (contact?.phone) {
            // smsto: es más estricto que sms: y limita el chooser a apps SMS reales
            const sms = `smsto:${contact.phone}?body=${encodeURIComponent(text)}`;
            window.location.href = sms;
            return;
        }
        // Último fallback: clipboard
        try {
            await navigator.clipboard.writeText(text);
        } catch {}
    };

    // Invitación múltiple. En Android `smsto:` admite varios números separados
    // por coma, lo que abre el chooser SMS con todos pre-rellenados. En iOS y
    // web caemos al SharePlugin (un solo diálogo de compartir) ya que `sms:`
    // multi-receptor no funciona consistentemente fuera de Android.
    const handleBulkInvite = async () => {
        const ids = Array.from(selectedInvites);
        if (ids.length === 0) return;
        const selected = withoutKaixo.filter(c => ids.includes(c.id) && c.phone);
        if (selected.length === 0) return;
        const text = inviteText;

        if (SharePlugin && checkIsNative()) {
            try {
                await SharePlugin.share({
                    title: 'KAIXO',
                    text: text + '\n\n' + selected.map(c => `${c.name}: ${c.phone}`).join('\n'),
                    dialogTitle: 'Compartir invitación'
                });
                exitBulkInvite();
                return;
            } catch {}
        }
        // Android: `smsto:phone1,phone2,...?body=...`
        const phones = selected.map(c => c.phone).join(',');
        const sms = `smsto:${phones}?body=${encodeURIComponent(text)}`;
        window.location.href = sms;
        exitBulkInvite();
    };

    const filterFn = (c) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        const name = (c.kaixoUser?.name || c.name || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        return name.includes(q) || phone.includes(q);
    };

    const filterGroupFn = (g) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (g.name || '').toLowerCase().includes(q);
    };

    // KAIXO Support se trata como un contacto KAIXO normal — antes vivía en
    // su propia sección "Asistencia". Lo prependemos a la lista de contactos
    // con KAIXO; visualmente lleva un anillo violeta en el avatar (en vez
    // del rótulo OFICIAL) para que se identifique sin romper la limpieza
    // visual de la lista.
    const kaixoListWithSupport = supportContact
        ? [{ ...supportContact, _isSupport: true }, ...withKaixo]
        : withKaixo;
    const filteredKaixo = kaixoListWithSupport.filter(filterFn);
    const filteredOther = withoutKaixo.filter(filterFn);
    const filteredGroups = (groups || []).filter(filterGroupFn);

    // Lista unificada: usuarios KAIXO + grupos en una sola lista. Orden:
    //   1) Favoritos (pinned) primero — tanto contactos como grupos.
    //   2) Después, por última interacción descendente (lastMessageTime).
    //   3) Los que no tienen conversación van al final, alfabético.
    // Esto reemplaza el listado "grupos primero, contactos después" anterior.
    const unifiedChatItems = useMemo(() => {
        const items = [];

        for (const c of filteredKaixo) {
            const conv = conversationByUserId.get(String(c.kaixoUser?.id || ''));
            const ts = conv?.lastMessageTime ? new Date(conv.lastMessageTime).getTime() : 0;
            items.push({
                _kind: 'kaixo',
                _key: c.id || (c._isSupport ? 'support' : c.kaixoUser?.id),
                _ts: ts,
                _name: c.kaixoUser?.name || c.name || '',
                _favorite: !!conv?.isFavorite,
                contact: c,
                conv
            });
        }
        for (const g of filteredGroups) {
            const ts = g.lastMessageTime ? new Date(g.lastMessageTime).getTime() : 0;
            items.push({
                _kind: 'group',
                _key: `group-${g._id}`,
                _ts: ts,
                _name: g.name || '',
                _favorite: !!g.isFavorite,
                group: g
            });
        }

        items.sort((a, b) => {
            // Favoritos arriba
            if (a._favorite !== b._favorite) return a._favorite ? -1 : 1;
            // Después por timestamp DESC
            if (a._ts !== b._ts) return b._ts - a._ts;
            // Los sin timestamp comparten _ts=0; alfabético entre ellos
            return a._name.localeCompare(b._name);
        });
        return items;
    }, [filteredKaixo, filteredGroups, conversationByUserId]);

    const isOnline = (id) => {
        try { return onlineUsers && onlineUsers.has && onlineUsers.has(String(id)); }
        catch { return false; }
    };

    return (
        <div className="phc-root">
            <div className="phc-header">
                <div className="phc-header-row">
                    <h2 className="phc-title">{labels.title}</h2>
                    <button
                        type="button"
                        className="phc-refresh-btn"
                        onClick={() => setRefreshTick(t => t + 1)}
                        disabled={loading}
                        title={labels.retry}
                        aria-label={labels.retry}
                    >
                        {loading ? '⏳' : '↻'}
                    </button>
                </div>
                <input
                    type="text"
                    className="phc-search"
                    placeholder={labels.searchPlaceholder}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {loading && (
                <div className="phc-state">
                    <div className="phc-spinner" />
                    <p>{labels.loading}</p>
                </div>
            )}

            {/* Web: si no hay nada que mostrar y estamos sin Capacitor, dejamos
                el área tranquila — ya no enseñamos el banner "instala la app
                móvil" ni el cartel grande, evita ruido al usuario. */}

            {!loading && permissionState === 'denied' && (
                <div className="phc-state phc-empty">
                    <div className="phc-empty-icon"><Icons.Users size={48} /></div>
                    <h3>{labels.permissionDeniedTitle}</h3>
                    <p>{labels.permissionDeniedDesc}</p>
                    <button className="phc-btn-primary" onClick={() => setRefreshTick(t => t + 1)}>
                        {labels.retry}
                    </button>
                </div>
            )}

            {!loading && permissionState === 'unknown' && (
                <div className="phc-state phc-empty">
                    <div className="phc-empty-icon"><Icons.Users size={48} /></div>
                    <h3>{labels.emptyTitle}</h3>
                    <p>{labels.emptyDesc}</p>
                    <button className="phc-btn-primary" onClick={() => setRefreshTick(t => t + 1)}>
                        {labels.permissionBtn}
                    </button>
                </div>
            )}

            {!loading && permissionState === 'empty' && (
                <div className="phc-state phc-empty">
                    <div className="phc-empty-icon"><Icons.Users size={48} /></div>
                    <h3>{labels.emptyTitle}</h3>
                    <p>{labels.emptyDesc}</p>
                </div>
            )}

            {!loading && errorMsg && (
                <div className="phc-error">{errorMsg}</div>
            )}

            {/* Mensajes recientes de personas que NO están en tu agenda.
                Aparecen siempre en el chats unificado, con pill de aviso y
                botón para añadirlas a contactos del móvil. */}
            {!loading && conversationsNotInContacts.length > 0 && (
                <>
                    <div className="phc-section-label phc-section-warning">
                        ⚠ {t.contacts?.unknownSenders || 'Mensajes de personas no en tu agenda'} ({conversationsNotInContacts.length})
                    </div>
                    <div className="phc-list">
                        {conversationsNotInContacts.map(conv => (
                            <div
                                key={`unk-${conv.id}`}
                                className="phc-row phc-row-kaixo phc-row-warning"
                                onClick={() => onSelectConversation && onSelectConversation(conv)}
                            >
                                <div className="phc-avatar-wrap">
                                    {conv.otherUser?.avatar ? (
                                        <img src={conv.otherUser.avatar} alt={conv.otherUser.name} />
                                    ) : (
                                        <div className="phc-avatar-fallback">
                                            {(conv.otherUser?.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div className="phc-row-main">
                                    <div className="phc-name-row">
                                        <span className="phc-name">{conv.otherUser?.name || '?'}</span>
                                        {conv.lastMessageTime && (
                                            <span className="phc-time">
                                                {formatRelativeDate(conv.lastMessageTime, currentUser?.language)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="phc-sub">{conv.lastMessage || ''}</div>
                                    {conv.otherUser?.phone && (
                                        <button
                                            type="button"
                                            className="phc-btn-add-contact"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onAddDeviceContact) onAddDeviceContact(conv.otherUser);
                                            }}
                                        >
                                            + {t.conv?.addToContacts || 'Añadir a contactos'}
                                        </button>
                                    )}
                                </div>
                                {conv.unreadCount > 0 && (
                                    <span className="phc-unread">{conv.unreadCount > 9 ? '9+' : conv.unreadCount}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {!loading && (permissionState === 'granted' || permissionState === 'unsupported') && (
                <>
                    {unifiedChatItems.length > 0 && (
                        <>
                            <div className="phc-section-label">
                                {labels.withKaixo} ({unifiedChatItems.length})
                            </div>
                            <div className="phc-list">
                                {/* Lista unificada: contactos KAIXO + grupos juntos.
                                    Orden: favoritos pinned arriba, después por última
                                    interacción DESC, los sin conv al final alfabético. */}
                                {unifiedChatItems.map(item => {
                                    if (item._kind === 'group') {
                                        const g = item.group;
                                        return (
                                            <div
                                                key={item._key}
                                                className="phc-row phc-row-kaixo phc-row-group"
                                                onClick={() => onSelectConversation && onSelectConversation({
                                                    id: g._id,
                                                    type: 'group',
                                                    name: g.name,
                                                    avatar: g.avatar || '',
                                                    members: g.members,
                                                    admins: g.admins,
                                                    lastMessage: g.lastMessageText || '',
                                                    isFormerMember: g.isFormerMember || false,
                                                    acknowledged: g.acknowledged !== false,
                                                    addedByName: g.addedByName || ''
                                                })}
                                            >
                                                <div className="phc-avatar-wrap">
                                                    {g.avatar ? (
                                                        <img src={g.avatar} alt={g.name} />
                                                    ) : (
                                                        <div className="phc-avatar-fallback phc-avatar-group">
                                                            {(g.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="phc-row-main">
                                                    <div className="phc-name-row">
                                                        <span className="phc-name">
                                                            {item._favorite && <span title="Favorito" style={{ marginRight: 4 }}>⭐</span>}
                                                            {g.name}
                                                            <span className="phc-badge phc-badge-group">GRUPO</span>
                                                        </span>
                                                        {g.lastMessageTime && (
                                                            <span className="phc-time">
                                                                {formatRelativeDate(g.lastMessageTime, currentUser?.language)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="phc-sub">
                                                        {g.lastMessageText || `${(g.members || []).length} miembros`}
                                                    </div>
                                                </div>
                                                {g.unreadCount > 0 && (
                                                    <span className="phc-unread">{g.unreadCount > 9 ? '9+' : g.unreadCount}</span>
                                                )}
                                            </div>
                                        );
                                    }
                                    // Kaixo
                                    const c = item.contact;
                                    const conv = item.conv;
                                    const isSupport = !!c._isSupport;
                                    return (
                                        <div
                                            key={item._key}
                                            className="phc-row phc-row-kaixo"
                                            onClick={() => handleOpenChat(c.kaixoUser)}
                                        >
                                            {/* Avatar con anillo: naranja para contactos KAIXO normales,
                                                violeta para KAIXO Support. */}
                                            <div className={`phc-avatar-wrap ${isSupport ? 'phc-avatar-wrap-support' : 'phc-avatar-wrap-kaixo'}`}>
                                                {c.kaixoUser?.avatar ? (
                                                    <img src={c.kaixoUser.avatar} alt={c.kaixoUser.name} />
                                                ) : (
                                                    <div className="phc-avatar-fallback">
                                                        {(c.kaixoUser?.name || c.name || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                {!isSupport && isOnline(c.kaixoUser?.id) && <span className="phc-online-dot" />}
                                            </div>
                                            <div className="phc-row-main">
                                                <div className="phc-name-row">
                                                    <span className="phc-name">
                                                        {item._favorite && <span title="Favorito" style={{ marginRight: 4 }}>⭐</span>}
                                                        {c.kaixoUser?.name || c.name}
                                                        {c.recentlyJoined && (
                                                            <span className="phc-badge phc-badge-new">{labels.newBadge}</span>
                                                        )}
                                                    </span>
                                                    {conv?.lastMessageTime && (
                                                        <span className="phc-time">
                                                            {formatRelativeDate(conv.lastMessageTime, currentUser?.language)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="phc-sub">
                                                    {conv?.lastMessage
                                                        ? conv.lastMessage
                                                        : (c.phone + (c.kaixoUser?.language ? ` · ${c.kaixoUser.language}` : ''))}
                                                </div>
                                            </div>
                                            {conv?.unreadCount > 0 ? (
                                                <span className="phc-unread">{conv.unreadCount > 9 ? '9+' : conv.unreadCount}</span>
                                            ) : (
                                                <Icons.ChevronRight size={18} color="#999" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {filteredKaixo.length === 0 && filteredOther.length > 0 && !search && (
                        <div className="phc-info">{labels.noKaixoYet}</div>
                    )}

                    {filteredOther.length > 0 && (
                        <>
                            <div className="phc-section-label phc-section-other">
                                {labels.otherContacts} ({filteredOther.length})
                            </div>
                            {/* Barra de acción bulk: estados separados según
                                bulkInviteMode. OFF: un único botón que activa
                                el modo. ON: contador + cancelar + invitar */}
                            <div className="phc-bulk-bar">
                                {!bulkInviteMode ? (
                                    <button
                                        type="button"
                                        className="phc-bulk-btn-start"
                                        onClick={() => setBulkInviteMode(true)}
                                    >
                                        📨 {labels.invite}
                                    </button>
                                ) : (
                                    <>
                                        <span className="phc-bulk-count">
                                            {selectedInvites.size} {selectedInvites.size === 1 ? 'seleccionado' : 'seleccionados'}
                                        </span>
                                        <button
                                            type="button"
                                            className="phc-bulk-btn-cancel"
                                            onClick={exitBulkInvite}
                                        >
                                            ✕
                                        </button>
                                        <button
                                            type="button"
                                            className="phc-bulk-btn-go"
                                            onClick={handleBulkInvite}
                                            disabled={selectedInvites.size === 0}
                                        >
                                            {labels.invite}{selectedInvites.size > 0 ? ` (${selectedInvites.size})` : ''}
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="phc-list">
                                {filteredOther.map(c => {
                                    const checked = selectedInvites.has(c.id);
                                    return (
                                        <div
                                            key={c.id}
                                            className={`phc-row phc-row-other ${bulkInviteMode ? 'phc-row-selectable' : ''} ${checked ? 'phc-row-selected' : ''}`}
                                            onClick={() => bulkInviteMode && toggleInviteSelected(c.id)}
                                            role={bulkInviteMode ? 'checkbox' : undefined}
                                            aria-checked={bulkInviteMode ? checked : undefined}
                                            tabIndex={bulkInviteMode ? 0 : undefined}
                                            onKeyDown={(e) => {
                                                if (bulkInviteMode && (e.key === 'Enter' || e.key === ' ')) toggleInviteSelected(c.id);
                                            }}
                                        >
                                            <div className="phc-avatar-wrap">
                                                <div className="phc-avatar-fallback phc-avatar-muted">
                                                    {(c.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="phc-row-main">
                                                <div className="phc-name">{c.name}</div>
                                                <div className="phc-sub">{c.phone}</div>
                                            </div>
                                            {bulkInviteMode && (
                                                <input
                                                    type="checkbox"
                                                    className="phc-row-check"
                                                    checked={checked}
                                                    onChange={() => toggleInviteSelected(c.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {search && filteredKaixo.length === 0 && filteredOther.length === 0 && (
                        <div className="phc-state phc-empty">
                            <p>{labels.noResults}</p>
                        </div>
                    )}
                </>
            )}

            {/* Pie de diagnóstico — solo en nativo, donde sí leemos del móvil */}
            {!loading && permissionState === 'granted' && (
                <div className="phc-diag">
                    📱 {diag.devCount} contactos del teléfono ·
                    {diag.syncOk === true && ' ✓ sincronizado'}
                    {diag.syncOk === false && ' ⚠ sin sincronizar'}
                    {diag.syncOk === null && ' ⏳ sincronizando...'}
                    {diag.getOk === false && ' · ⚠ backend no responde'}
                </div>
            )}
        </div>
    );
}

export default PhoneContactsView;
